import SearchPanel from '@components/searchPanel'
import { Grid } from '@mui/material'
import * as common from '@common/common'
import { GlobalContext } from '@providers/globalStore'
import { InitialSearchPanelState, SearchPanelContext } from '@providers/SearchPanelProvider'
import SearchPanelReducer, { SearchPanelState } from '@reducers/searchPanelReducer'
import moment from 'moment'
import { Fragment, useContext, useReducer, useRef, useState } from 'react'
import { NavigateFunction } from 'react-router-dom'
import useTableView from '@hooks/useTableView'
import { useTranslation } from 'react-i18next'
import FormStatusLabel from '@components/FormStatusLabel'
import GetValueWithKey from '@utils/getValueWithKey'

import { FormWouListModel, WouFilterOptions } from '@services/model/form/form.WOU.model'
import FormWOUService from '@services/formService/form.WOU.service'
import { NavigateTo } from '@utils/navigate'
import { RightsCategory } from '@services/model/form/form.model'
import { KeyValPair } from '@models/common'
import { GetDistrictOptions } from '@utils/districtOptions'
import TableBox from '@components/table/HoverTableBox'
import { BaseFilterOptions } from '@services/model/base.model'

export default function AllRecord(props: { showMyRecord?: boolean }) {
  const refMounted = useRef(false)
  const { state: globalState, userInfo } = useContext(GlobalContext)
  const [state, dispatch] = useReducer<React.Reducer<SearchPanelState, any>>(
    SearchPanelReducer,
    InitialSearchPanelState,
  )

  const { t } = useTranslation()
  const [extraListInfo, setExtraListInfo] = useState<WouFilterOptions>({
    submittedByList: [],
    approvedByList: [],
    formIdList: [],
    workOrderIdList: [],
    parentFormIdList: [],
  })

  const getExtraListInfo = async () => {
    let extraInfo = await FormWOUService.GetWouFilterOption(props.showMyRecord)
    setExtraListInfo(extraInfo)
    refMounted.current = true
  }

  const [TableView, reload] = useTableView<FormWouListModel>({
    headers: [
      {
        key: 'formStatusShortName',
        desc: t('General Status', { ns: common.i18nFormNS }),
        renderCell: (record: FormWouListModel) => (
          <Fragment>
            <FormStatusLabel
              label={record.formStatusShortName}
              color={record.formStatusColor || ''}
            />
          </Fragment>
        ),
      },
      {
        key: 'formStatus',
        desc: t('Submission Status', { ns: common.i18nFormNS }),
        renderCell: (record: FormWouListModel) => <Fragment>{record.formStatus}</Fragment>,
      },
      {
        key: 'formId',
        desc: t('Form ID', { ns: common.i18nFormNS }),
        renderCell: (record: FormWouListModel) => <Fragment>{record.reportNo}</Fragment>,
      },
      {
        key: 'workOrderId',
        desc: t('Work Order No', { ns: common.i18nFormNS }),
        renderCell: (record: FormWouListModel) => <Fragment>{record.workOrderNo}</Fragment>,
      },
      {
        key: 'typeOfWorks',
        desc: t('Type Of Work', { ns: common.i18nFormNS }),
        renderCell: (record: FormWouListModel) => (
          <Fragment>{record.typeOfWorks ? 'M&M Works' : 'Non-M&M Works'}</Fragment>
        ),
      },
      {
        key: 'submittedBy',
        desc: t('Submitted By', { ns: common.i18nFormNS }),
        renderCell: (record: FormWouListModel) => (
          <Fragment>{GetValueWithKey(record.submittedBy, globalState.userMetaList ?? [])}</Fragment>
        ),
      },
      {
        key: 'approvedBy',
        desc: t('Approved By', { ns: common.i18nFormNS }),
        renderCell: (record: FormWouListModel) => (
          <Fragment>{GetValueWithKey(record.approvedBy, globalState.userMetaList ?? [])}</Fragment>
        ),
      },
      {
        key: 'updatedAt',
        desc: t('Last Update', { ns: common.i18nFormNS }),
        renderCell: (record: FormWouListModel) => (
          <Fragment>{record.updatedAt ? moment(record.updatedAt).format() : ''}</Fragment>
        ),
      },
    ],
    onRowClick: function (navigate: NavigateFunction, record: FormWouListModel) {
      NavigateTo(navigate, '/works-order-update/:id', { id: record.formId })
    },
    mountedRef: refMounted,
    reloadCallback: async (pagination: object, sorting: object | undefined, cancelToken: any) => {
      if (refMounted.current) {
        sessionStorage.setItem('WOU', JSON.stringify(state.filterResult))
        let resp = await FormWOUService.GetWouList(
          {
            ...state.filterResult,
            sorting: sorting,
            pagination: pagination,
          },
          cancelToken,
          props.showMyRecord || false,
        )
        return resp
      }
    },
  })

  return (
    <Grid component="main" container padding={1}>
      <SearchPanelContext.Provider value={{ state, dispatch }}>
        <SearchPanel
          dispatch={dispatch}
          addUrl="/booking/new"
          onSearch={reload}
          onInitReload={reload}
          onToggleFilterDrawer={getExtraListInfo}
          mountedRef={refMounted}
          excludeStatus={true}
          criteria={[
            {
              key: 'formStatus',
              desc: 'Submission Status',
              type: 'NumberSelectionFilter',
              multiple: true,
              valueList:
                globalState.formStatusList
                  ?.filter((x) => x.actionCode.includes(RightsCategory.FORM_WOU))
                  .map((x) => {
                    return {
                      key: x.id,
                      value: x.actionName,
                    }
                  }) || [],
              defaultValue:
                sessionStorage?.WOU !== 'undefined' && sessionStorage?.WOU !== undefined
                  ? JSON.parse(sessionStorage?.WOU)?.formStatus?.value
                  : [],
            },
            {
              key: 'formId',
              desc: 'Form ID',
              type: 'StringSelectionFilter',
              multiple: true,
              valueList: extraListInfo.formIdList,
              defaultValue:
                sessionStorage?.WOU !== 'undefined' && sessionStorage?.WOU !== undefined
                  ? JSON.parse(sessionStorage?.WOU)?.formId?.value
                  : [],
            },
            {
              key: 'workOrderId',
              desc: 'Works Order No',
              type: 'StringSelectionFilter',
              multiple: true,
               valueList: globalState.worksOrderList?.filter((x) =>
                extraListInfo.workOrderIdList?.includes(x.id),
              )?.map(({ id, workOrderNo}) => ({ key: id, value: workOrderNo})),
              defaultValue:
                sessionStorage?.SCR !== 'undefined' && sessionStorage?.SCR !== undefined
                  ? JSON.parse(sessionStorage?.SCR)?.workOrderId?.value
                  : [],
            },
            // {
            //   key: 'typeOfWorks',
            //   desc: 'Type of Works',
            //   type: 'NumberSelectionFilter',
            //   multiple: true,
            //   valueList: [
            //     {
            //       key: 'true',
            //       value: 'M&M Works',
            //     },
            //     {
            //       key: 'false',
            //       value: 'Non-M&M Works',
            //     },
            //   ],
            //   defaultValue:
            //     sessionStorage?.WOI !== 'undefined' && sessionStorage?.WOI !== undefined
            //       ? JSON.parse(sessionStorage?.WOI)?.typeOfWorks?.value
            //       : [],
            // },
            {
              key: 'submittedBy',
              desc: 'Submitted By',
              type: 'StringSelectionFilter',
              multiple: true,
              valueList: globalState.userMetaList?.filter((x) =>
                extraListInfo.submittedByList?.includes(x.key),
              ),
              defaultValue:
                sessionStorage?.WOU !== 'undefined' && sessionStorage?.WOU !== undefined
                  ? JSON.parse(sessionStorage?.WOU)?.submittedBy?.value
                  : [],
            },
            {
              key: 'approvedBy',
              desc: 'Approved By',
              type: 'StringSelectionFilter',
              multiple: true,
              valueList: globalState.userMetaList?.filter((x) =>
                extraListInfo.approvedByList?.includes(x.key),
              ),
            },
            {
              key: 'updatedAt',
              desc: 'Last Update',
              type: 'DateFilter',
              defaultOperator:
                sessionStorage?.WOU !== 'undefined' && sessionStorage?.WOU !== undefined
                  ? JSON.parse(sessionStorage?.WOU)?.updatedAt?.operator
                  : 'in',
              defaultFrom:
                sessionStorage?.WOU !== 'undefined' && sessionStorage?.WOU !== undefined
                  ? JSON.parse(sessionStorage?.WOU)?.updatedAt?.value?.min
                  : '',
              defaultTo:
                sessionStorage?.WOU !== 'undefined' && sessionStorage?.WOU !== undefined
                  ? JSON.parse(sessionStorage?.WOU)?.updatedAt?.value?.max
                  : '',
            },
          ]}
        />
        <TableView />
      </SearchPanelContext.Provider>
    </Grid>
  )
}
